import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function SimpleInterestCalculator() {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [time, setTime] = useState("");
  const [result, setResult] = useState<{ interest: number; total: number } | null>(null);

  const calculate = () => {
    const P = parseFloat(principal);
    const R = parseFloat(rate);
    const T = parseFloat(time);

    if (isNaN(P) || isNaN(R) || isNaN(T) || P <= 0 || R <= 0 || T <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const interest = (P * R * T) / 100;
    const total = P + interest;

    setResult({ interest, total });
    toast.success("Interest calculated!");
  };

  const reset = () => {
    setPrincipal("");
    setRate("");
    setTime("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Simple Interest Calculator" calculatorPath="/calculator/simple-interest">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Simple Interest Calculator</h1>
          <p className="text-muted-foreground">
            Calculate interest earned or owed on a principal amount over time
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: I = (P × R × T) / 100</CardTitle>
            <CardDescription>
              Where I is interest, P is principal, R is annual rate (%), and T is time (years)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="principal">Principal Amount ($)</Label>
                <Input
                  id="principal"
                  type="number"
                  placeholder="10000"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate">Annual Interest Rate (%)</Label>
                <Input
                  id="rate"
                  type="number"
                  placeholder="5"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time Period (years)</Label>
                <Input
                  id="time"
                  type="number"
                  placeholder="3"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculate} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">Reset</Button>
            </div>

            {result && (
              <>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 border-emerald-500/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Results</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>Interest Earned:</strong> ${result.interest.toFixed(2)}
                      </p>
                      <CopyResultButton value={result.interest.toFixed(2)} />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg break-all overflow-wrap-anywhere">
                        <strong>Total Amount:</strong> ${result.total.toFixed(2)}
                      </p>
                      <CopyResultButton value={result.total.toFixed(2)} />
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["simple-interest"]} trigger={result.total} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the principal amount (initial investment or loan)</p>
                <p>2. Enter the annual interest rate as a percentage</p>
                <p>3. Enter the time period in years</p>
                <p>4. Click Calculate to see interest earned and total amount</p>
              </CardContent>
            </Card>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Example</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p><strong>Problem:</strong> You invest $10,000 at 5% annual interest for 3 years.</p>
                <p><strong>Solution:</strong></p>
                <div className="pl-4 space-y-1">
                  <p>I = (P × R × T) / 100</p>
                  <p>I = ($10,000 × 5 × 3) / 100</p>
                  <p>I = $1,500</p>
                  <p>Total = P + I = $10,000 + $1,500 = $11,500</p>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
